import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { configureAuth, ProtectedRoute, PublicRoute } from '@puglieseweb/shared-react-auth';
import { AuthProvider, useAuthContext } from '@/contexts/AuthContext';
import { authConfig } from '@/lib/authConfig';
import LoginPage, { PostLoginRedirect } from '@/pages/auth/LoginPage';
import SignupPage from '@/pages/auth/SignupPage';
import { isAppDomain, isBuildHost, isTenantCaptureHost, launchUrl, getTenantSlug, rememberedTenantSlug } from '@/utils/subdomain';
import { CaptureAddWorkflow } from '@/capture/CaptureAddWorkflow';
import { AddProvider } from '@/capture/AddContext';
import DocViewerPage from '@/pages/docs/DocViewerPage';
import { WorkspaceProvider } from '@/contexts/WorkspaceContext';
import MFASetupPage from '@/pages/auth/MFASetupPage';
import { TenantShell } from '@/components/layout/TenantShell';
import { NeutralHostRoutes } from '@/components/layout/NeutralHostRoutes';
import { ReaderShell } from '@/components/layout/ReaderShell';
import { ConsolePillars } from '@/components/layout/ConsolePillars';
import { TenantNeutralGate } from '@/components/layout/TenantNeutralGate';
import LaunchPage from '@/pages/LaunchPage';
import HubPage from '@/pages/HubPage';
import NotificationsPage from '@/pages/notifications/NotificationsPage';
import SettingsPage from '@/pages/settings/SettingsPage';
import WorkspaceTab from '@/pages/settings/WorkspaceTab';
import MembersTab from '@/pages/settings/MembersTab';
import GroupsTab from '@/pages/settings/GroupsTab';
import SpacesTab from '@/pages/settings/SpacesTab';
import CreateWorkspacePage from '@/pages/workspace/CreateWorkspacePage';
import JoinPage from '@/pages/workspace/JoinPage';
import WelcomePage from '@/pages/workspace/WelcomePage';
import { loadPendingInvite } from '@/utils/pendingInvite';
import { shouldShowWelcome } from '@/utils/welcome';
import GitHubCallbackPage from '@/pages/settings/GitHubCallbackPage';
import GitHubConnectionPage from '@/pages/settings/GitHubConnectionPage';
import TaxonomyPage from '@/pages/settings/TaxonomyPage';
import ProjectsSettingsPage from '@/pages/settings/ProjectsSettingsPage';
import RepositoryRolesPage from '@/pages/settings/RepositoryRolesPage';
import SanitizationTab from '@/pages/settings/SanitizationTab';
import ApiKeysTab from '@/pages/settings/ApiKeysTab';
import SecuritySettingsPage from '@/pages/settings/SecuritySettingsPage';
import SpecEditorPage from '@/pages/specs/SpecEditorPage';
import NewSpecPage from '@/pages/specs/NewSpecPage';
import StalenessReportPage from '@/pages/staleness/StalenessReportPage';
import BacklogPage from '@/pages/backlog/BacklogPage';
import GenerateSpecPage from '@/pages/specs/GenerateSpecPage';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import ScannerPage from '@/pages/scanner/ScannerPage';
import AiChatPage from '@/pages/ai/AiChatPage';
import PlanPage from '@/pages/ai/PlanPage';
import PlanLayoutPage from '@/pages/ai/PlanLayoutPage';
import BuildLayoutPage from '@/pages/ai/BuildLayoutPage';
import BuildStandardsPage from '@/pages/ai/BuildStandardsPage';
import BuildConnectPage from '@/pages/ai/BuildConnectPage';
import StandardsLayoutPage from '@/pages/standards/StandardsLayoutPage';
import GuardrailsTab from '@/pages/ai/settings/GuardrailsTab';
import GuardrailRecordPage from '@/pages/guardrails/GuardrailRecordPage';
import TechStackTab from '@/pages/ai/settings/TechStackTab';
import ClaudeMdTab from '@/pages/ai/settings/ClaudeMdTab';
import RulesTab from '@/pages/ai/settings/RulesTab';
import PromptsTab from '@/pages/ai/settings/PromptsTab';
import { teamSetupRoutes } from '@/pages/ai/settings/teamSetupRoutes';
import VerifyHubPage from '@/pages/verify/VerifyHubPage';
import FitnessPage from '@/pages/verify/FitnessPage';
import TestReportsPage from '@/pages/verify/TestReportsPage';
import CiStatusPage from '@/pages/verify/CiStatusPage';
import ApisPage from '@/pages/verify/ApisPage';
import BookViewerPage from '@/pages/docs/BookViewerPage';
import InsightsPage from '@/pages/insights/InsightsPage';
import BookmarksPage from '@/pages/bookmarks/BookmarksPage';
import ChangesListPage from '@/pages/changes/ChangesListPage';
import ChangePage from '@/pages/changes/ChangePage';
import { ProjectPathResolver } from '@/components/layout/ProjectPathResolver';

// The pool, the front door and shared-auth's API base (lib/authConfig).
configureAuth(authConfig());

/** Redirect that preserves the query string and hash (Navigate drops them). */
function RedirectTo({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

/** Legacy /execute/guardrails/:id → /docs/guardrails/:id, id and query intact
 *  (the permalink published fitness scripts printed before Checks moved). */
function GuardrailRecordRedirect() {
  const { guardrailId } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/docs/guardrails/${guardrailId}${search}${hash}`} replace />;
}

/**
 * The tenant's whole app lives on its root host ({slug}.systemdox.com) now. The
 * Build console host ({slug}.build) keeps resolving but only forwards there,
 * path intact, so old bookmarks survive the consolidation.
 */
function BuildHostRedirect() {
  const { pathname, search, hash } = useLocation();
  const slug = getTenantSlug() ?? rememberedTenantSlug();
  useEffect(() => {
    if (slug) window.location.replace(launchUrl(slug, `${pathname}${search}${hash}`));
  }, [pathname, search, hash, slug]);
  return null;
}

/**
 * The tenant capture host ({slug}.capture) forwards to the root too. Its Add
 * workflow moved to /add, so the capture root and its /add alias both land
 * there; every other path carries over unchanged.
 */
function CaptureHostRedirect() {
  const { pathname, search, hash } = useLocation();
  const slug = getTenantSlug() ?? rememberedTenantSlug();
  useEffect(() => {
    if (!slug) return;
    const dest = pathname === '/' || pathname === '/add' ? '/add' : pathname;
    window.location.replace(launchUrl(slug, `${dest}${search}${hash}`));
  }, [pathname, search, hash, slug]);
  return null;
}

/** Legacy /specs/:draftId deep links land on the Requirements pillar (#538). */
function SpecIdRedirect() {
  const { draftId } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/requirements/${draftId}${search}${hash}`} replace />;
}

/**
 * The Add capture workflow, rendered as a content page inside TenantShell on the
 * tenant root. Keeps the narrow mobile-first column the capture surface used,
 * and owns its own scroll (TenantShell's <main> is bounded).
 */
function AddScreen() {
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-lg py-4">
        <CaptureAddWorkflow />
      </div>
    </div>
  );
}

function AppRoutes() {
  const { isAuthenticated, isLoading, user } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();

  // Host consolidation: the tenant's whole app lives on its root host
  // ({slug}.systemdox.com). The old Build console ({slug}.build) and tenant
  // capture ({slug}.capture) hosts only forward there now, and the neutral
  // app.systemdox.com stays the login entry (its post-login hop lands on the
  // root via the TenantNeutralGate). Everything else — the root host in
  // production, ?tenant on localhost in dev — renders the full app.
  const buildHost = isBuildHost();
  const tenantCaptureHost = isTenantCaptureHost();
  const neutralApp = isAppDomain();
  const fullAppHost = !buildHost && !tenantCaptureHost && !neutralApp;

  // A parked invite (#495) must be resolved on THIS origin — localStorage
  // doesn't follow across tenant subdomains — so route to /join before anything
  // else. Only meaningful on the full-app host; the forwarding/neutral hosts run
  // their own navigation.
  const onJoinPage = location.pathname === '/join';
  const hasPendingInvite = loadPendingInvite() !== null;
  useEffect(() => {
    if (!fullAppHost || !isAuthenticated || isLoading || onJoinPage) return;
    if (hasPendingInvite) navigate('/join', { replace: true });
  }, [fullAppHost, isAuthenticated, isLoading, onJoinPage, hasPendingInvite, navigate]);

  // One-time post-signup welcome (#496). Never competes with a pending invite —
  // invited users land in their team instead.
  const onWelcomePage = location.pathname === '/welcome';
  useEffect(() => {
    if (!fullAppHost || !isAuthenticated || isLoading || onWelcomePage || onJoinPage || hasPendingInvite) return;
    if (shouldShowWelcome(user)) navigate('/welcome', { replace: true });
  }, [fullAppHost, isAuthenticated, isLoading, onWelcomePage, onJoinPage, hasPendingInvite, user, navigate]);

  // Build console host + tenant capture host: forward to the tenant root,
  // keeping the path so old bookmarks still resolve.
  if (buildHost) {
    return (
      <Routes>
        <Route path="*" element={<BuildHostRedirect />} />
      </Routes>
    );
  }
  if (tenantCaptureHost) {
    return (
      <Routes>
        <Route path="*" element={<CaptureHostRedirect />} />
      </Routes>
    );
  }

  // Neutral entry (app.systemdox.com, or localhost without ?tenant): its whole
  // route table — auth pages, the GitHub App callback, and the gate-forwarding
  // funnel onto the tenant root host — lives in NeutralHostRoutes.
  if (neutralApp) {
    return <NeutralHostRoutes />;
  }

  // Full app — the tenant root host ({slug}.systemdox.com), and ?tenant on
  // localhost in dev. Serves everything: the LaunchPage hub, the Add · Docs
  // content pages, and the console pillars (Plan, Connect, Checks, Insights, …).
  return (
    <>
    <PostLoginRedirect />
    <Routes>
      {/* Guest-only routes */}
      <Route
        path="/login"
        element={
          <PublicRoute isAuthenticated={isAuthenticated} isLoading={isLoading} homePath="/">
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute isAuthenticated={isAuthenticated} isLoading={isLoading} homePath="/">
            <SignupPage />
          </PublicRoute>
        }
      />

      {/* Accept-invite — reachable logged in or out (#495) */}
      <Route path="/join" element={<JoinPage />} />

      {/* Post-signup welcome — one-time, skippable (#496) */}
      <Route
        path="/welcome"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <WelcomePage />
          </ProtectedRoute>
        }
      />

      {/* MFA setup — requires auth but NOT MFA gate (so users can enroll) */}
      <Route
        path="/mfa-setup"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <MFASetupPage />
          </ProtectedRoute>
        }
      />

      {/* /launch is the post-login redirectPath; on the root host it IS home. */}
      <Route path="/launch" element={<Navigate to="/" replace />} />

      {/* Path-addressable project views (/p/{slug}/…): resolve the slug,
          persist the selection, continue to the wrapped path. */}
      <Route
        path="/p/:projectSlug/*"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <ProjectPathResolver />
          </ProtectedRoute>
        }
      />

      {/* Book viewer — full-screen reading layout, outside TenantShell but under
          its own ReaderShell so it keeps the one canonical SystemDoxHeader on
          top (no lifecycle rail/bottom nav — the reader has its own sidebar).
          Opened from a repo card on the Docs hub; needs the WorkspaceProvider
          for the repo list (connection check) and trees, same as /docs. */}
      <Route
        path="/book/:owner/:repo/*"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <TenantNeutralGate>
              <ReaderShell>
                <WorkspaceProvider>
                  <BookViewerPage />
                </WorkspaceProvider>
              </ReaderShell>
            </TenantNeutralGate>
          </ProtectedRoute>
        }
      />

      {/* ── One shell for every authenticated tenant page ──
          TenantShell owns the shared chrome — the SystemDoxHeader and the
          BottomNav bar (the coarse quick-access) — plus the light always-on
          providers (Tenant, Project, notifications). Auth wraps the whole shell
          here; each route group below adds only the extra providers/scroll it
          needs. The BottomNav renders only inside TenantShell, so it is the same
          on the hub, the Add/Docs pages and every console pillar. MFA is opt-in
          (Settings ▸ Security) — no gate forces enrollment before the product. */}
      <Route
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            {/* AddProvider sits above the whole shell so elements captured on
                /add (photos, notes, in-flight uploads) survive navigating to
                any other tenant page and back. */}
            <AddProvider>
              <TenantShell />
            </AddProvider>
          </ProtectedRoute>
        }
      >
        {/* Hub — the tenant's home dashboard (owns its own scroll column). */}
        <Route index element={<HubPage />} />

        {/* Add · Docs — the capture/content pages. The Docs page needs the
            WorkspaceProvider (repos, trees, publish status), so it mounts with
            the /docs route only, keeping Add visits from firing those fetches. */}
        <Route path="add" element={<AddScreen />} />
        <Route
          path="docs"
          element={
            <WorkspaceProvider>
              <div className="flex-1 min-h-0">
                <DocViewerPage />
              </div>
            </WorkspaceProvider>
          }
        >
          <Route path="requirements" element={null} />
          <Route path="components" element={null} />
          <Route path="design" element={null} />
        </Route>
        {/* Publishing a repo's docs is done from the book editor that opens
            when you pick a repo (its Publish/Settings actions), so these two
            no longer have a repo to act on — they only ever knew one from
            in-memory state. Old links land back on the hub. */}
        <Route path="docs/settings" element={<RedirectTo to="/docs" />} />
        <Route path="docs/publish" element={<RedirectTo to="/docs" />} />
        {/* Gaps merged into the Insights pillar (formerly Learn) — the gap
            analysis is a section of that page, so both legacy paths forward
            there (path/query preserved). */}
        <Route path="gaps" element={<RedirectTo to="/insights" />} />
        <Route path="learn" element={<RedirectTo to="/insights" />} />

        {/* Console pillars — Workspace + PublishJob providers, the console's own
            scroll column and the publish toast (ConsolePillars); the neutral
            gate travels with them. Pathless layout: the root "/" is the hub
            above, so there is no index here. */}
        <Route element={<ConsolePillars />}>
        <Route path="repos" element={<Navigate to="/settings/github" replace />} />
        {/* Requirements & Design pillars — the lists live inside the Docs
            page as pinned sections (/docs/requirements, /docs/design); the
            editors below stay full-page. */}
        <Route path="requirements" element={<RedirectTo to="/docs/requirements" />} />
        <Route path="requirements/new" element={<NewSpecPage />} />
        <Route path="requirements/generate" element={<GenerateSpecPage />} />
        <Route path="requirements/:draftId" element={<SpecEditorPage />} />
        <Route path="design" element={<RedirectTo to="/docs/design" />} />
        <Route path="design/new" element={<NewSpecPage docType="design" />} />
        <Route path="design/:draftId" element={<SpecEditorPage />} />
        <Route path="specs" element={<RedirectTo to="/docs/requirements" />} />
        <Route path="specs/new" element={<RedirectTo to="/requirements/new" />} />
        <Route path="specs/generate" element={<RedirectTo to="/requirements/generate" />} />
        <Route path="specs/:draftId" element={<SpecIdRedirect />} />
        <Route path="graph" element={<Navigate to="/" replace />} />
        <Route path="staleness" element={<StalenessReportPage />} />
        {/* Changes — one page per change under Docs › Requirements: the brief
            (decision, contract, behaviour, plan) and the release view, each with
            its state read from GitHub (ADR-218; the process is ADR-201 Amendment 2). */}
        <Route path="changes" element={<ChangesListPage />} />
        <Route path="changes/:owner/:repo/:slug" element={<ChangePage />} />
        <Route path="backlog" element={<BacklogPage />} />
        <Route path="claude-md" element={<Navigate to="/docs/model-settings" replace />} />
        <Route path="scanner" element={<ScannerPage />} />
        <Route path="workflow" element={<Navigate to="/connect" replace />} />
        {/* Legacy /planning flow — a pre-pillar duplicate of /plan; the pages
            are gone, only the redirects remain for old bookmarks. */}
        <Route path="planning" element={<RedirectTo to="/plan" />} />
        <Route path="planning/new" element={<RedirectTo to="/plan" />} />

        {/* Legacy redirects (Define → Standards → Foundation → Build → Execute) */}
        <Route path="foundation" element={<RedirectTo to="/connect" />} />
        <Route path="foundation/tech-stack" element={<RedirectTo to="/docs/tech-stack" />} />
        <Route path="foundation/guardrails" element={<RedirectTo to="/docs/guardrails" />} />
        <Route path="foundation/model-settings" element={<RedirectTo to="/docs/model-settings" />} />
        <Route path="foundation/prompts" element={<RedirectTo to="/docs/prompts" />} />
        <Route path="standards" element={<RedirectTo to="/connect" />} />
        <Route path="standards/stack" element={<RedirectTo to="/docs/tech-stack" />} />
        <Route path="standards/guardrails" element={<RedirectTo to="/docs/guardrails" />} />
        <Route path="standards/ai-config" element={<RedirectTo to="/docs/model-settings" />} />
        <Route path="standards/rules-prompts" element={<RedirectTo to="/docs/prompts" />} />
        <Route path="standards/claude-md" element={<Navigate to="/docs/model-settings" replace />} />
        <Route path="define" element={<Navigate to="/connect" replace />} />
        <Route path="define/stack" element={<Navigate to="/docs/tech-stack" replace />} />
        <Route path="define/guardrails" element={<Navigate to="/docs/guardrails" replace />} />
        <Route path="define/claude-md" element={<Navigate to="/docs/model-settings" replace />} />
        <Route path="define/workflow" element={<Navigate to="/connect" replace />} />

        {/* Legacy /build/* — the pillar became the Connect pillar at /connect,
            and the standards it configured moved under /docs. */}
        <Route path="build" element={<RedirectTo to="/connect" />} />
        <Route path="build/tech-stack" element={<RedirectTo to="/docs/tech-stack" />} />
        <Route path="build/guardrails" element={<RedirectTo to="/docs/guardrails" />} />
        <Route path="build/model-settings" element={<RedirectTo to="/docs/model-settings" />} />
        <Route path="build/prompts" element={<RedirectTo to="/docs/prompts" />} />
        <Route path="build/rules" element={<RedirectTo to="/docs/rules" />} />
        <Route path="build/chat" element={<RedirectTo to="/connect/chat" />} />
        <Route path="build/plan" element={<RedirectTo to="/plan" />} />
        <Route path="build/execute" element={<RedirectTo to="/connect" />} />
        <Route path="build/verify" element={<RedirectTo to="/verify" />} />

        {/* Plan pillar (top-level, formerly /build/plan) */}
        <Route path="plan" element={<PlanLayoutPage />}>
          <Route index element={<PlanPage />} />
        </Route>

        {/* Connect pillar (route /connect, formerly /execute) — the MCP
            connection guide, the Agent Skills export, and a live preview of what
            a connected assistant receives. "Standards" was the old display
            label; the pillar is "Connect" now, and the standards it used to tab
            between moved under /docs (below). */}
        <Route path="connect" element={<BuildLayoutPage />}>
          <Route index element={<BuildStandardsPage />} />
          <Route path="chat" element={<AiChatPage />} />
        </Route>

        {/* Workspace standards — the editors moved off the Connect pillar to
            live under /docs, where the Docs home already lists them ("What your
            AI reads"). Pathless layout (StandardsLayoutPage) so they share the
            tab strip; kept inside ConsolePillars for the Workspace/PublishJob
            providers the editors need. The route/key names stay stable (the
            backend recommendation payloads and the MCP tool contract reference
            them) even though the URL moved off /execute. */}
        <Route element={<StandardsLayoutPage />}>
          <Route path="docs/tech-stack" element={<TechStackTab />} />
          <Route path="docs/guardrails" element={<GuardrailsTab />} />
          {/* The permalink every published fitness script prints when it fails —
              the only route a blocked engineer has back to the governing record. */}
          <Route path="docs/guardrails/:guardrailId" element={<GuardrailRecordPage />} />
          <Route path="docs/model-settings" element={<ClaudeMdTab />} />
          <Route path="docs/prompts" element={<PromptsTab />} />
          <Route path="docs/rules" element={<RulesTab />} />
          {/* ADR-217: Docs › Team setup, its four tabs at /docs/team-setup/<tab>. Docs › Skills'
              address opens its Skills tab; ADR-216's skill pages keep their addresses. */}
          {teamSetupRoutes()}
        </Route>

        {/* Legacy /execute/* — the pillar became "Connect" at /connect and its
            standards tabs moved under /docs. Keep these so old bookmarks and the
            permalinks already printed by published fitness scripts still land. */}
        <Route path="execute" element={<RedirectTo to="/connect" />} />
        <Route path="execute/chat" element={<RedirectTo to="/connect/chat" />} />
        <Route path="execute/tech-stack" element={<RedirectTo to="/docs/tech-stack" />} />
        <Route path="execute/guardrails" element={<RedirectTo to="/docs/guardrails" />} />
        <Route path="execute/guardrails/:guardrailId" element={<GuardrailRecordRedirect />} />
        <Route path="execute/model-settings" element={<RedirectTo to="/docs/model-settings" />} />
        <Route path="execute/prompts" element={<RedirectTo to="/docs/prompts" />} />
        <Route path="execute/rules" element={<RedirectTo to="/docs/rules" />} />
        <Route path="execute/plan" element={<RedirectTo to="/plan" />} />
        <Route path="execute/verify" element={<RedirectTo to="/verify" />} />

        {/* Validate pillar (top-level, formerly /build/verify) */}
        <Route path="verify" element={<Outlet />}>
          <Route index element={<VerifyHubPage />} />
          <Route path="fitness" element={<FitnessPage />} />
          <Route path="tests" element={<TestReportsPage />} />
          <Route path="ci" element={<CiStatusPage />} />
          <Route path="apis" element={<ApisPage />} />
        </Route>

        {/* Bookmarks — saved documents for quick access */}
        <Route path="bookmarks" element={<BookmarksPage />} />

        {/* Insights pillar — gap analysis + the failure-to-check loop */}
        <Route path="insights" element={<InsightsPage />} />

        {/* Docs is a top-level content route under TenantShell now (above);
            only the legacy /publish alias into the Docs library stays. */}
        <Route path="publish" element={<RedirectTo to="/docs" />} />

        {/* Legacy /ai redirects */}
        <Route path="ai" element={<Navigate to="/connect" replace />} />
        <Route path="ai/settings/*" element={<Navigate to="/connect" replace />} />
        <Route path="ai/plan" element={<Navigate to="/plan" replace />} />
        <Route path="ai/execute" element={<Navigate to="/connect" replace />} />
        <Route path="ai/chat" element={<Navigate to="/connect/chat" replace />} />
        <Route path="ai/guardrails" element={<Navigate to="/docs/guardrails" replace />} />
        <Route path="ai/agent" element={<Navigate to="/plan" replace />} />
        <Route path="admin" element={<AdminDashboard />} />
        <Route path="workspaces/new" element={<CreateWorkspacePage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="settings" element={<SettingsPage />}>
          <Route index element={<Navigate to="/settings/general" replace />} />
          <Route path="general" element={<WorkspaceTab />} />
          <Route path="workspace" element={<Navigate to="/settings/general" replace />} />
          <Route path="members" element={<MembersTab />} />
          <Route path="groups" element={<GroupsTab />} />
          <Route path="spaces" element={<SpacesTab />} />
          <Route path="repositories" element={<Navigate to="/settings/github" replace />} />
          <Route path="github" element={<GitHubConnectionPage />} />
          <Route path="taxonomy" element={<TaxonomyPage />} />
          <Route path="projects" element={<ProjectsSettingsPage />} />
          <Route path="repo-roles" element={<RepositoryRolesPage />} />
          <Route path="sanitization" element={<SanitizationTab />} />
          <Route path="doc-repos" element={<Navigate to="/settings/repo-roles" replace />} />
          <Route path="ci-integration" element={<Navigate to="/settings/repo-roles" replace />} />
          <Route path="api-keys" element={<ApiKeysTab />} />
          <Route path="security" element={<SecuritySettingsPage />} />
          {/* Connect — MCP connection guide (moved from /build; absorbed the
              claude-ai stub, whose label collided with Execute ▸ Model Settings) */}
          <Route path="connect" element={<BuildConnectPage />} />
          <Route path="build" element={<Navigate to="/settings/connect" replace />} />
          <Route path="claude-ai" element={<Navigate to="/settings/connect" replace />} />
        </Route>
        <Route path="settings/github/callback" element={<GitHubCallbackPage />} />
        {/* Unknown paths recover to the hub. The neutral host forwards deep
            links here verbatim (NeutralHostRoutes catch-all, #885), so a
            mistyped or stale URL must land somewhere with chrome — before
            this fallback it rendered a bare blank page. */}
        <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Route>
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
